
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CanvasElement } from './CanvasMetadataManager';

interface CanvasElementEditModalProps {
  element: CanvasElement | null;
  isVisible: boolean;
  onSave: (elementId: string, updatedData: Partial<CanvasElement>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const CanvasElementEditModal: React.FC<CanvasElementEditModalProps> = ({
  element,
  isVisible,
  onSave,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [editedElement, setEditedElement] = useState<Partial<CanvasElement>>({});
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (element) {
      setEditedElement({
        title: element.title || '',
        content: element.content || '',
        imageUrl: element.imageUrl || '',
      });
      setImagePreview(element.imageUrl || '');
    } else {
      setEditedElement({ title: '', content: '', imageUrl: '' });
      setImagePreview('');
    }
  }, [element]);

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert('Image file size should be less than 5MB.');
      return;
    }

    setIsUploadingImage(true);
    
    try {
      // Create a local URL for the image
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
      setEditedElement(prev => ({ ...prev, imageUrl }));
      
      // In a real application, you would upload to a server here
      // For now, we'll use the local object URL
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview('');
    setEditedElement(prev => ({ ...prev, imageUrl: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    if (element) {
      onSave(element.id, editedElement);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
    else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSave();
  };

  if (!isVisible || !element) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        zIndex: 4000, backdropFilter: 'blur(4px)',
      }}
      onClick={onCancel} onKeyDown={handleKeyDown} tabIndex={-1}
    >
      <div
        className="modal-content"
        style={{
          background: 'linear-gradient(135deg, rgba(26, 42, 108, 0.95) 0%, rgba(58, 97, 134, 0.95) 100%)',
          border: '1px solid rgba(76, 201, 240, 0.3)',
          borderRadius: '16px', padding: '24px', maxWidth: '500px',
          width: '90%', maxHeight: '80vh', overflow: 'auto',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid rgba(76, 201, 240, 0.2)' }}>
          <h3 style={{ margin: 0, color: '#ffffff', fontSize: '20px', fontWeight: 'bold' }}>
            ✏️ {t('canvas.editModal.title', { elementType: element.type })}
          </h3>
        </div>

        <div className="modal-body">
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px', fontWeight: 'bold', opacity: 0.9 }}>
              {t('canvas.editModal.elementTitle')} *
            </label>
            <input
              type="text"
              value={editedElement.title || ''}
              onChange={(e) => setEditedElement(prev => ({ ...prev, title: e.target.value }))}
              placeholder={t('canvas.editModal.elementTitle')}
              disabled={isLoading}
              style={{ width: '100%', padding: '10px 12px', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(76, 201, 240, 0.3)', borderRadius: '8px', color: '#ffffff', fontSize: '14px', outline: 'none' }}
            />
          </div>

          {element.type === 'character' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px', fontWeight: 'bold', opacity: 0.9 }}>
                {t('canvas.editModal.characterImage')}
              </label>
              
              {/* Image Preview */}
              {imagePreview && (
                <div style={{ marginBottom: '12px', position: 'relative', display: 'inline-block' }}>
                  <img 
                    src={imagePreview} 
                    alt="Character preview"
                    style={{ 
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '50%', 
                      objectFit: 'cover',
                      border: '2px solid rgba(76, 201, 240, 0.3)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    disabled={isLoading || isUploadingImage}
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-5px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#ff6b6b',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
              
              {/* File Upload */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  disabled={isLoading || isUploadingImage}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || isUploadingImage}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(76, 201, 240, 0.2)',
                    border: '1px solid rgba(76, 201, 240, 0.4)',
                    borderRadius: '6px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {isUploadingImage ? '⏳' : '📁'} 
                  {isUploadingImage ? 'Uploading...' : 'Choose Image'}
                </button>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                  JPG, PNG, GIF up to 5MB
                </span>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: '#ffffff', fontSize: '14px', fontWeight: 'bold', opacity: 0.9 }}>
              {t('canvas.editModal.elementContent')}
            </label>
            <textarea
              value={editedElement.content || ''}
              onChange={(e) => setEditedElement(prev => ({ ...prev, content: e.target.value }))}
              placeholder={t('canvas.editModal.elementContent')}
              disabled={isLoading}
              rows={5}
              style={{ width: '100%', padding: '10px 12px', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(76, 201, 240, 0.3)', borderRadius: '8px', color: '#ffffff', fontSize: '14px', outline: 'none', resize: 'vertical' }}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid rgba(76, 201, 240, 0.2)' }}>
          <button onClick={onCancel} disabled={isLoading} style={{ padding: '10px 20px', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(76, 201, 240, 0.3)', borderRadius: '8px', color: '#ffffff', cursor: 'pointer' }}>
            {t('canvas.editModal.cancel')}
          </button>
          <button onClick={handleSave} disabled={isLoading || !editedElement.title?.trim()} style={{ padding: '10px 20px', background: 'linear-gradient(45deg, #4361ee, #4cc9f0)', border: 'none', borderRadius: '8px', color: '#ffffff', cursor: 'pointer' }}>
            {isLoading ? t('canvas.editModal.saving') : t('canvas.editModal.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CanvasElementEditModal;
